import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MaterialImports } from 'src/app/shared/imports/material.imports';
import { SelectionModel } from '@angular/cdk/collections';
import { ConsultationPublique } from '../../../shared/models/consultation-publique.models';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ValeurDomaineDto } from 'src/app/shared/models/domaines.dto';
import { DomainesService } from 'src/app/shared/services/domaines.service';
import { TranslateModule } from '@ngx-translate/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ConsultationsPubliqueService } from '../../../shared/services/pilotage-consultation-publique.service';
import { MessageService } from '@igo2/core/message';
import { LanguageService } from '@igo2/core/language';

@Component({
    selector: 'pilotage-consultation-publique',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        ...MaterialImports
    ],
    templateUrl: './pilotage-consultation-publique.component.html',
    styleUrls: ['./pilotage-consultation-publique.component.scss']
})
export class PilotageConsultationPubliqueComponent
  implements OnInit, AfterViewInit
{
  toutesLesRegions: ValeurDomaineDto[] = [];
  dataSource = new MatTableDataSource<ConsultationPublique>([]);
  selection  = new SelectionModel<ConsultationPublique>(true, []);
  formGroup!: FormGroup;
  editingIndex: number | null = null;  // null = ajout, 0+ = édition
  messagesDeValidation: { [key: string]: string } = {};
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  formColumns = [
    'selectForm',
    'regionsForm',
    'dateDebutForm',
    'dateFinForm',
    'nomForm',
    'actionsForm'
  ];
  displayedColumns = [
    'select',
    'regions',
    'dateDebut',
    'dateFin',
    'nom',
    'actions'
  ];

  constructor(
    private fb: FormBuilder,
    private domaineService: DomainesService,
    private service: ConsultationsPubliqueService,
    private messageService: MessageService,
    private languageService: LanguageService
  ) {}

  ngOnInit() {
    this.messagesDeValidation.auMoinsUneRegionRequise =
      this.languageService.translate.instant('consultationPublique.pilotage.messages.AtLeastOneSelectedF').replace('{0}', 'région');
    this.messagesDeValidation.nomConsultationRequis = this.languageService.translate.instant('consultationPublique.pilotage.messages.nomConsultationRequis');
    this.messagesDeValidation.dateDebutRequise = this.languageService.translate.instant('consultationPublique.pilotage.messages.dateDebutRequise');
    this.messagesDeValidation.dateFinRequise = this.languageService.translate.instant('consultationPublique.pilotage.messages.dateFinRequise');
    this.messagesDeValidation.dateDebutSuperieurFin = this.languageService.translate.instant('consultationPublique.pilotage.messages.dateDebutSuperieurFin');
    this.messagesDeValidation.regionDejaUtilisee = this.languageService.translate.instant('consultationPublique.pilotage.messages.regionDejaUtilisee');

    this.formGroup = this.fb.group({
      id: [null],
      regions: [[], [Validators.required, this.regionUniqueValidator()]],
      dateDebut: [null, [Validators.required]],
      dateFin: [null, [Validators.required]],
      nom: ['', [Validators.required, Validators.maxLength(100)]]
    });

    this.formGroup.setValidators(this.dateRangeValidator());

    this.service.getAll().subscribe({
      next: result => {
        this.dataSource.data = this.sortConsultations(result.valeur);
      },
      error: err => console.error('GET failed', err)
    });

    this.domaineService.obtenirValeurs('REGION_ADMIN').subscribe(
      resp => this.toutesLesRegions = resp.valeursDomaine
    );
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  private dateRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const debut = group.get('dateDebut')?.value;
      const fin = group.get('dateFin')?.value;
      return debut && fin && fin < debut
        ? { dateInvalide: true }
        : null;
    };
  }
  private regionUniqueValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const selected = control.value ?? [];
      const autres = this.dataSource.data
        .filter((_, i) => i !== this.editingIndex)
        .flatMap(row => row.regions);
      const conflit = selected.some((r: string) => autres.includes(r));
      return conflit ? { regionDejaUtilisee: true } : null;
    };
  }


  startEdit(row: ConsultationPublique, idx: number) {
    this.editingIndex = idx;
    this.formGroup.patchValue(row);
  }

  clearFormFields() {
    this.editingIndex = null;
    this.formGroup.reset({
        id: null,
        nom: '',
        dateDebut: null,
        dateFin: null,
        regions: [] as string[]
      }, { emitEvent: false }
    );

    Object.values(this.formGroup.controls).forEach(control => {
      control.setErrors(null, { emitEvent: false });
      control.markAsPristine();
      control.markAsUntouched();
    });
    this.formGroup.setErrors(null, { emitEvent: false });
    this.formGroup.markAsPristine();
    this.formGroup.markAsUntouched();
    this.selection.clear();
  }

  addOrUpdate() {

    const consultation : ConsultationPublique = this.formToModel();
    if (!consultation) {
      this.messageService.error('Données du formulaire invalides', 'Erreur');
      return;
    }

    if (this.editingIndex === null) {
      // ADD
      const payload: any = {
        nom: consultation.nom,
        dateDebut: consultation.dateDebut.toISOString(),
        dateFin: consultation.dateFin.toISOString(),
        regions: consultation.regions
      };
      this.service.add(payload).subscribe({
        next: added => {
          this.dataSource.data = [...this.dataSource.data, added.valeur];
          this.applySort();
          this.clearFormFields();
        },
        error: err => console.error('POST failed', err)
      });
    } else {
      // UPDATE
      const payload: any = {
        id: consultation.id,
        nom: consultation.nom,
        dateDebut: consultation.dateDebut.toISOString(),
        dateFin: consultation.dateFin.toISOString(),
        regions: consultation.regions
      };
      this.service.update(payload).subscribe({
        next: updated => {
          const data = this.dataSource.data;
          data[this.editingIndex!] = updated.valeur;
          this.dataSource.data = data;
          this.applySort();
          this.clearFormFields();
       },
       error: err => console.error('PUT failed', err)
     });
    }
  }

  deleteSelected() {
    const idsToDelete = this.dataSource.data
      .filter(r => this.selection.isSelected(r))
      .map(r => r.id) as number[];

    this.service.delete(idsToDelete).subscribe({
      next: deleted => {
        if (deleted.messages?.[0]?.msg == 'EnfantExiste' && deleted.messages[0]?.type == 'warning') {
          this.messageService.error("Des commentaires sont associés à cette consultation.", "Suppression annulée");
          return;
        }
        this.dataSource.data = this.dataSource.data.filter(r => !this.selection.isSelected(r));
        this.selection.clear();
        this.applySort();
      },
      error: err => {
        console.error('DELETE failed', err);
        this.messageService.error('Erreur lors de la suppression', 'Erreur');
      }
    });
  }

  isAllSelected(): boolean {
    const numSel = this.selection.selected.length;
    const numRows= this.dataSource.data.length;
    return numSel === numRows && numRows > 0;
  }

  toggleSelection() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach(r => this.selection.select(r));
  }

  getDescriptionRegion(code: string): string {
    const dto = this.toutesLesRegions.find(r => r.valeur === code);
    return dto ? dto.description : '';
  }

  displayRegions(codes: string[]): string {
    return codes
      .map(c => this.toutesLesRegions.find(d => d.valeur===c)?.description || '')
      .filter(x => !!x)
      .join(', ');
  }

  private sortConsultations(list: ConsultationPublique[]): ConsultationPublique[] {
    return list.slice().sort((a, b) => {
      // 1) dateFin desc
      const aDateFin =  new Date(a.dateFin).getTime();
      const bDateFin =  new Date(b.dateFin).getTime();
      if (aDateFin !== bDateFin) {
        return bDateFin - aDateFin;
      }
      // 2) dateDebut desc
      const aDateDebut = new Date(a.dateDebut).getTime();
      const bDateDebut = new Date(b.dateDebut).getTime();
      if (aDateDebut !== bDateDebut) {
        return bDateDebut - aDateDebut;
      }
      // 3) id desc
      return ( a.id < b.id) ? 1 : ( a.id > b.id) ? -1 : 0;
    });
  }

  private applySort() {
    this.dataSource.data = this.sortConsultations(this.dataSource.data);
  }

  private formToModel(): ConsultationPublique | null {
    const v = this.formGroup.value;

    if (!v) return null;
    const dateDebut = v.dateDebut ? (v.dateDebut instanceof Date ? v.dateDebut : new Date(v.dateDebut)) : null;
    const dateFin = v.dateFin ? (v.dateFin instanceof Date ? v.dateFin : new Date(v.dateFin)) : null;

    return {
      id: v.id == null ? 0 : v.id,
      nom: v.nom,
      dateDebut: dateDebut as Date,
      dateFin: dateFin as Date,
      regions: v.regions
    } as ConsultationPublique;
  }
}
